import React from 'react';

import { Group, Paper, Stack, Text, Loader, Divider, Flex } from '@mantine/core';
import { useLocalization } from 'context/localization-context';
import usePackage from 'hooks/package/usePackage';
import { getAttendanceRuleSummary, getEndConditions } from 'utils/billing.helpers';

interface PackageDetailPanelProps {
  packageId: string;
}

const PackageDetailPanel: React.FC<PackageDetailPanelProps> = ({ packageId }): JSX.Element => {
  const { formatCurrency } = useLocalization();
  const { data: packageData, isLoading } = usePackage(packageId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader />
      </div>
    );
  }

  if (!packageData) {
    return <></>;
  }

  const endConditions = getEndConditions(packageData.data.attendanceRules, packageData.data.type);

  return (
    <Flex gap='xl' justify='center' align='flex-start' direction='row' wrap='nowrap' m={0} bg='gray.1'>
      <Paper shadow='sm' radius='md' withBorder sx={{ flexBasis: '60%}' }}>
        <Stack spacing='xs' py='sm'>
          <Stack spacing={3}>
            <Group grow noWrap px='md' ta='center' sx={{ whiteSpace: 'nowrap' }}>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Event Type
              </Text>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Attendance
              </Text>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Validity
              </Text>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Delay Start
              </Text>
            </Group>
            <Divider />
          </Stack>
          {getAttendanceRuleSummary(packageData.data.attendanceRules).map(ar => (
            <Group key={ar.key} grow noWrap px='md' ta='center'>
              <Text size='sm' fw='bold' data-cy='eventTypePackageDP'>
                {ar.name}
              </Text>
              <Text size='sm' data-cy='attendancePackageDP'>
                {ar.attendance}
              </Text>
              <Text size='sm' data-cy='validityPackageDP'>
                {ar.validity}
              </Text>
              <Text size='sm' data-cy='delayStartPackageDP'>
                {ar.delay}
              </Text>
            </Group>
          ))}
          {endConditions && (
            <>
              <Divider />
              <Text size='sm' px='md' ta='center' data-cy='endConditionsPackageDP'>
                {endConditions}
              </Text>
            </>
          )}
        </Stack>
      </Paper>

      <Paper shadow='sm' radius='md' withBorder sx={{ flexBasis: '30%}' }}>
        <Stack spacing='xs' py='sm'>
          <Stack spacing={3}>
            <Group grow noWrap px='md' ta='center' sx={{ whiteSpace: 'nowrap' }}>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Billing Plan
              </Text>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Total Price
              </Text>
              <Text c='dimmed' fs='italic' size='sm' fw='bold'>
                Installments
              </Text>
            </Group>
            <Divider />
          </Stack>
          {packageData.data.billingPlans.map(bp => (
            <Group key={bp.name} grow noWrap px='md' ta='center'>
              <Text size='sm' fw='bold' data-cy='bpNamePackageDP'>
                {bp.name}
              </Text>
              <Text size='sm' data-cy='totalPricePackageDP'>
                {formatCurrency(bp.totalPrice / 100, {
                  minimumFractionDigits: 2,
                })}
              </Text>
              <Text size='sm' data-cy='installmentsPricePackageDP'>
                {bp.numberOfInstallments}
              </Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Flex>
  );
};

export default PackageDetailPanel;
